/**
 * AiEvidenceCard — small structured "what data backed this answer" tile.
 *
 * Used by AiChatPanel after every AI response. Renders the tool name, a
 * range label (e.g. "Today (America/Los_Angeles)"), and a list of metrics.
 * When the underlying tool returned `available: false`, we render a muted
 * "data not tracked yet" state instead of the metrics.
 */
import React from 'react';

export default function AiEvidenceCard({ card }) {
  if (!card) return null;
  const unavailable = card.available === false;
  return (
    <div
      data-testid="ai-evidence-card"
      className={
        'rounded-xl border p-4 text-sm shadow-sm transition ' +
        (unavailable
          ? 'border-zinc-200 bg-zinc-50 text-zinc-500'
          : 'border-emerald-200 bg-emerald-50/40 text-zinc-800')
      }
    >
      <div className="mb-1 flex items-center justify-between">
        <div className="font-semibold tracking-tight">{card.title}</div>
        {card.range_label ? (
          <div className="text-[11px] uppercase tracking-wide text-zinc-500">
            {card.range_label}
          </div>
        ) : null}
      </div>

      {unavailable ? (
        <div className="italic">
          {card.note || 'Data not tracked yet.'}
        </div>
      ) : (
        <div className="space-y-1">
          {Object.entries(card.metrics || {}).map(([label, value]) => (
            <div
              key={label}
              className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1 last:border-b-0"
            >
              <span className="text-zinc-500">{label}</span>
              <span className="font-medium tabular-nums">{String(value)}</span>
            </div>
          ))}
          {card.note ? (
            <div className="pt-2 text-xs text-zinc-500">{card.note}</div>
          ) : null}
        </div>
      )}

      <div className="mt-2 text-[10px] uppercase tracking-wide text-zinc-400">
        Source: {card.tool}
      </div>
    </div>
  );
}
